// ─── Role-based permissions ──────────────────────────────────────────────────
// Centralized permission checks for frontend visibility and route guards.

export type AppRole =
  | 'EXECUTIVE'
  | 'SENIOR_ENGINEERING'
  | 'SALES'
  | 'ENGINEERING'
  | 'PROCUREMENT'
  | 'OPERATIONS'
  | 'WAREHOUSE'
  | 'INSTALL'
  | 'PM'

// Which roles can see financial data in-context (BOM costs, GP%, project contract
// totals). This is about *cost visibility*, not access to the global money modules —
// see PROCUREMENT_NAV_ROLES / INVOICING_ROLES for those.
const FINANCIAL_ROLES: AppRole[] = ['EXECUTIVE', 'SENIOR_ENGINEERING', 'SALES', 'ENGINEERING', 'PROCUREMENT']

// Who gets the /procurement module. Mirrors PROC_ROLES in routes/procurement.ts.
const PROCUREMENT_NAV_ROLES: AppRole[] = ['EXECUTIVE', 'PROCUREMENT']

// Who gets /invoicing. Mirrors FINANCE_ROLES in routes/invoicing.ts, which is
// EXECUTIVE-only — previously SALES/ENGINEERING/PROCUREMENT saw the nav item and
// every API call 403'd.
const INVOICING_ROLES: AppRole[] = ['EXECUTIVE']

// Which roles can see admin sections (reporting, settings)
const ADMIN_ROLES: AppRole[] = ['EXECUTIVE']

// Which roles may write BOM lines (add/edit/delete/split/duplicate/reorder).
// Mirrors the requireRole(...WRITER_ROLES, 'ENGINEERING', 'PROCUREMENT') gate on the
// BOM line routes in routes/projects.ts — keep the two in step so the UI never offers
// an action the API will reject.
const BOM_WRITE_ROLES: AppRole[] = ['EXECUTIVE', 'ENGINEERING', 'PROCUREMENT']

// Which roles can see procurement write actions (create PO, edit BOM financials)
const PROCUREMENT_WRITE_ROLES: AppRole[] = ['EXECUTIVE', 'PROCUREMENT']

// Which roles can see savings data (original cost, savings column, savings tab BOM view)
const SAVINGS_ROLES: AppRole[] = ['EXECUTIVE', 'PROCUREMENT']

// Which roles can edit the Knowledge Base
const KB_EDIT_ROLES: AppRole[] = ['EXECUTIVE', 'SENIOR_ENGINEERING']

// Which roles can view the Knowledge Base (read-only)
const KB_VIEW_ROLES: AppRole[] = ['EXECUTIVE', 'SENIOR_ENGINEERING', 'ENGINEERING']

// Which roles can approve estimates
const ESTIMATE_APPROVE_ROLES: AppRole[] = ['EXECUTIVE', 'SENIOR_ENGINEERING']

// Which roles can interact with project snapshots (view, create, diff, revert)
const SNAPSHOT_ROLES: AppRole[] = ['EXECUTIVE', 'PROCUREMENT']

export function canSeeFinancials(role?: string): boolean {
  return FINANCIAL_ROLES.includes(role as AppRole)
}

export function canSeeAdmin(role?: string): boolean {
  return ADMIN_ROLES.includes(role as AppRole)
}

export function canWriteProcurement(role?: string): boolean {
  return PROCUREMENT_WRITE_ROLES.includes(role as AppRole)
}

export function canSeeSavings(role?: string): boolean {
  return SAVINGS_ROLES.includes(role as AppRole)
}

export function canSeeProcurement(role?: string): boolean {
  return PROCUREMENT_NAV_ROLES.includes(role as AppRole)
}

export function canSeeInvoicing(role?: string): boolean {
  return INVOICING_ROLES.includes(role as AppRole)
}

export function canEditBom(role?: string): boolean {
  return BOM_WRITE_ROLES.includes(role as AppRole)
}

export function canEditKnowledgeBase(role?: string): boolean {
  return KB_EDIT_ROLES.includes(role as AppRole)
}

export function canUseSnapshots(role?: string): boolean {
  return SNAPSHOT_ROLES.includes(role as AppRole)
}

export function canSeeQuoting(_role?: string): boolean {
  return false // quoting/estimating modules hidden until ready for release
}

export function canViewKnowledgeBase(role?: string): boolean {
  return KB_VIEW_ROLES.includes(role as AppRole)
}

export function canApproveEstimate(role?: string): boolean {
  return ESTIMATE_APPROVE_ROLES.includes(role as AppRole)
}

// Nav items visible per role — Sales group is always present (spec §9.0)
export function getVisibleNavGroups(role?: string) {
  const r = role as AppRole
  const showProcurement = canSeeProcurement(r)
  const showInvoicing = canSeeInvoicing(r)
  const showAdmin = canSeeAdmin(r)
  const showKb = canViewKnowledgeBase(r)

  // Sales group at top (per spec §9.0: Sales above Operations)
  const salesItems: { name: string; to: string; iconName: string }[] = [
    { name: 'Estimates', to: '/estimates', iconName: 'TrendingUp' },
  ]
  if (showKb) {
    salesItems.push({ name: 'Knowledge Base', to: '/knowledge-base', iconName: 'BookOpen' })
  }

  const groups: { label: string; items: { name: string; to: string; iconName: string }[]; beta?: boolean }[] = [
    { label: 'Sales', items: salesItems, beta: true },
    {
      label: 'Operations',
      items: [
        { name: 'Dashboard', to: '/', iconName: 'LayoutDashboard' },
        { name: 'Projects', to: '/projects', iconName: 'FolderKanban' },
        { name: 'Warehouse', to: '/warehouse', iconName: 'ScanBarcode' },
        { name: 'Asset Tracking', to: '/tracking', iconName: 'Radio' },
      ],
    },
  ]

  // Each item is gated independently so we never show a link whose API will 403.
  const financialItems = [
    ...(showProcurement ? [{ name: 'Procurement', to: '/procurement', iconName: 'Package' }] : []),
    ...(showInvoicing ? [{ name: 'Invoicing', to: '/invoicing', iconName: 'FileText' }] : []),
    ...(showAdmin ? [{ name: 'Reporting', to: '/reporting', iconName: 'BarChart2' }] : []),
  ]
  if (financialItems.length > 0) {
    groups.push({ label: 'Financial', items: financialItems })
  }

  if (showAdmin) {
    groups.push({
      label: 'Admin',
      items: [
        { name: 'Settings', to: '/settings', iconName: 'Settings' },
      ],
    })
  }

  return groups
}

// Routes restricted by role. Each prefix maps to the same predicate that gates its
// nav item, so a hand-typed URL can't reach a module the sidebar hides.
const ADMIN_ROUTES = ['/settings', '/reporting']
const PROCUREMENT_ROUTES = ['/procurement']
const INVOICING_ROUTES = ['/invoicing']
const KB_ROUTES = ['/knowledge-base']

export function canAccessRoute(path: string, role?: string): boolean {
  if (ADMIN_ROUTES.some(r => path.startsWith(r)) && !canSeeAdmin(role)) return false
  if (PROCUREMENT_ROUTES.some(r => path.startsWith(r)) && !canSeeProcurement(role)) return false
  if (INVOICING_ROUTES.some(r => path.startsWith(r)) && !canSeeInvoicing(role)) return false
  if (KB_ROUTES.some(r => path.startsWith(r)) && !canViewKnowledgeBase(role)) return false
  return true
}
